import {
  applySkillAdditions,
  removeSkillsByName,
  type SkillAddition,
} from "@/client/pages/coach/optimize/coach-skill-additions";
import { resolveRevertText } from "@/client/pages/coach/optimize/original-content";
import type { OptimizationSuggestion } from "@/client/pages/dashboard/ai-optimization/review";
import { useCoachOptimizationStore } from "@/client/stores/coach-optimization";
import { useResumeStore } from "@/client/stores/resume";

/**
 * Accept / reject / edit behaviour for coach suggestions, in one place.
 *
 * Extracted from CoachReviewPanel when suggestions moved out of that single panel and into the
 * resume section each one belongs to. The rules encoded here (revert from the run-start snapshot,
 * remove skills by name, keep an accepted card's edits written through to the resume) are subtle
 * and were each a bug once — duplicating them per call site is how they come back.
 *
 * Docs: lms-backend/docs/plans/2026-09-23-coach-resume-improvement.md
 */
export const useCoachSuggestionActions = () => {
  const acceptedIds = useCoachOptimizationStore((state) => state.acceptedIds);
  const acceptSuggestion = useCoachOptimizationStore((state) => state.acceptSuggestion);
  const rejectSuggestion = useCoachOptimizationStore((state) => state.rejectSuggestion);
  const setDraft = useCoachOptimizationStore((state) => state.setDraft);
  const drafts = useCoachOptimizationStore((state) => state.drafts);
  const originalValues = useCoachOptimizationStore((state) => state.originalValues);

  const setValue = useResumeStore((state) => state.setValue);
  const resumeData = useResumeStore((state) => state.resume.data);

  /**
   * Append a skill the résumé already evidences but does not list.
   *
   * Written through `setValue` on the whole items array rather than a per-item path, because adding
   * an item is a list mutation and the suggestion machinery only knows how to replace a scalar at a
   * fieldPath. That gap is why the optimizer previously had no way to add a skill at all — leaving
   * `skillOverlap`, the heaviest résumé-movable component, with almost no lever.
   */
  const skillItemsOf = (document: { sections?: { skills?: { items?: unknown[] } } }) =>
    document.sections?.skills?.items ?? [];

  /**
   * Mark the résumé as AI-optimized, once, on the first accepted change.
   *
   * This is what the "AI Optimized" badge on the résumé list reads. Stamped HERE rather than when an
   * optimization run starts, because a student who opened the optimizer and rejected every suggestion
   * has a résumé the AI did not write — badging that would be a claim about the document that the
   * document does not support.
   *
   * Written through `setValue` like any other edit, so it rides the same autosave and cannot end up
   * persisted separately from the change it describes. Idempotent: the timestamp is the FIRST accept,
   * so later accepts leave it alone and the badge does not start reporting "optimized seconds ago" for
   * a run that happened last week.
   *
   * ⚠️ NOT REVERSED BY REJECT. Rejecting restores that one field's text; it does not establish that
   * nothing else was AI-written, and unwinding the stamp would need the accepted set to be empty AND
   * every field to match its run-start snapshot. The honest reading of the flag is "AI suggestions were
   * applied to this résumé at some point", which survives a later reject.
   */
  const stampAiOptimized = () => {
    const metadata = (resumeData as { metadata?: { aiOptimizedAt?: string | null } } | undefined)
      ?.metadata;

    if (metadata?.aiOptimizedAt) return;

    setValue("metadata.aiOptimizedAt", new Date().toISOString());
  };

  const onAcceptSkill = (addition: SkillAddition) => {
    const next = applySkillAdditions(resumeData as never, [addition]);
    setValue("sections.skills.items", skillItemsOf(next));
    stampAiOptimized();
    acceptSuggestion(addition.id);
  };

  const onRejectSkill = (addition: SkillAddition) => {
    // Removed by NAME: the item on the résumé carries its own generated cuid2, so matching on the
    // addition's id would silently remove nothing.
    if (acceptedIds.includes(addition.id)) {
      const next = removeSkillsByName(resumeData as never, [addition.name]);
      setValue("sections.skills.items", skillItemsOf(next));
    }
    rejectSuggestion(addition.id);
  };

  const onAccept = (suggestion: OptimizationSuggestion) => {
    const text = drafts[suggestion.id]?.trim() || suggestion.suggestedText;
    setValue(suggestion.fieldPath, text);
    stampAiOptimized();
    acceptSuggestion(suggestion.id);
  };

  const onReject = (suggestion: OptimizationSuggestion) => {
    // Only revert when this suggestion is what put the new text there. Blindly
    // writing previousText back would also wipe out a manual edit the student made
    // after accepting.
    //
    // Restores from the RUN-START SNAPSHOT, not `suggestion.previousText`.
    // `previousText` is re-derived from the résumé on every run, so once a run's rewrite
    // has been accepted and saved, the next run's `previousText` IS that rewrite — and
    // reject would "restore" it, permanently losing the student's own wording. See
    // pages/coach/optimize/original-content.ts.
    if (acceptedIds.includes(suggestion.id)) {
      setValue(suggestion.fieldPath, resolveRevertText(suggestion, originalValues));
    }
    rejectSuggestion(suggestion.id);
  };

  const onEdit = (suggestion: OptimizationSuggestion, value: string) => {
    setDraft(suggestion.id, value);

    // Already accepted? Keep the resume in step with the edit so the preview and the
    // projected score reflect what the student is actually typing.
    if (acceptedIds.includes(suggestion.id)) {
      setValue(suggestion.fieldPath, value);
    }
  };

  return {
    acceptedIds,
    drafts,
    resumeData,
    // Exposed so the diff card can render the struck-out "before" half from the same
    // run-start snapshot that `onReject` restores from. Reading it live off `resumeData`
    // instead made the before/after halves identical the moment a card was accepted.
    originalValues,
    onAccept,
    onReject,
    onEdit,
    onAcceptSkill,
    onRejectSkill,
  };
};
